/********************************************************
 * TODO: 1. CONTROL VISIBILITY NONE ON SLIDES COMPONENT *
 *          a. control for init & switch ☑              *
 *          b. control for animation switch +/-         *
 *       2. RESIZE WITH INLINE STYLES ☑                 *
 *       3. Add fade animation +/-                      *
 *       4. Add class active ☑                          *
 *       5. Add slots for visible controls ☑            *
 *          a. buttons icon ☑                           *
 *       6. Add slots for clicked pagination ☑          *
 *          a. styling class for navigation +/-         *
 *       7. Add drag and swipe control                  *
 *         a. desktop control ☑                        *
 *         b. mobile control ☑                         *
 *       8. Thumbnails                                  *
 *       9. Bind controls between swipers               *
 *       10. No loop mode ☑                            *
 *       11. Slide per view ☑                          *
 *       12. Breackpoints ☑                            *
 *       13. Effect fade                                *
 *       14. Hide navigation button when data           *
 *           length < 2                                *
 *******************************************************/

// TODO: implement flip
// export type TEffect = 'slide' | 'fade' | 'flip';

import Pagination from './Pagination';
import CntrlBtn from './ControlButton';
import VLazyImage from 'v-lazy-image';
import Vue, { CreateElement, VNode } from 'vue';
import { arrayIsEmpty, NoCache, chunkRecursivity, objectIsEmpty } from '@/utils';
import { Component, Watch } from 'vue-property-decorator';
import { ISwiper, Listener, ISwiperOptions, TVisible, TVector } from '@/types';
import { createListeners, removeListeners } from './helper';
import { mixins } from 'vue-class-component';
import Options from './SwiperOptions';
import mergeDeep from 'lodash.merge';

@Component({
  name: 'Swiper',
})
export default class Swiper extends mixins(Vue, Options) implements ISwiper {
  private swiperOptions!: ISwiperOptions;
  private display: TVisible = 'hidden';
  private fadeInAnimation: boolean | null = null;
  private fadeOutAnimation: boolean | null = null;
  private instanceActiveIndex: number = 0;
  private instanceContentLoaded: boolean = false;
  private instanceData: any = null;
  private instancePreviousIndex: number | null = null;
  private instanceRenderSlides: boolean = false;
  private instancePerView: number | null = null;
  private dragged: boolean = false;
  private offset: number = 0;
  private resized: number | null = null;
  private vector: TVector = null;
  private observer: MutationObserver | null = null;
  private requestAnimationID: any = null;


  @NoCache
  public get isDragged(): boolean {
    return this.dragged;
  }
  public set isDragged(value: boolean) {
    this.dragged = value;
  }

  @NoCache
  public get perView(): number {
    if (this.swiperOptions.slidesPerView === 'auto') { return 1; }
    return this.instancePerView || this.swiperOptions.slidesPerView;
  }
  public set perView(value: number) {
    this.instancePerView = value;
  }

  @NoCache
  public get chunks(): VNode[][] | VNode[] {
    return chunkRecursivity(this.childs, this.perView);
  }

  @NoCache
  public get childs(): VNode[] {
    return !!this.$slots?.child ? this.$slots.child : [];
  }

  @NoCache
  public get calculatePages(): number {
    return this.iData.length || Math.ceil(this.childs.length / this.perView);
  }

  @NoCache
  public get prevElement(): Element | false {
    return typeof this.instancePreviousIndex === 'number'
      ? (this as any).wrapperElement.children.item(this.instancePreviousIndex)
      : false;
  }

  @NoCache
  public get activeElement(): HTMLElement | null {
    return this.wrapperElement.children.item(this.instanceActiveIndex) as HTMLElement;
  }

  public get fadeAnimation(): boolean {
    return !!this.fadeInAnimation || !!this.fadeOutAnimation;
  }

  public get wrapperElement(): HTMLElement {
    return this.$refs.wrapper as HTMLElement;
  }

  public get swiperSpeed(): number {
    return this.swiperOptions.speed;
  }

  public get computedWidth(): number {
    if (!!this.resized) {
      return this.resized;
    }
    const width = !!this.$el && window.getComputedStyle(this.$el).width;
    return !!width ? parseInt(width, 10) : 0;
  }

  public get width(): string {
    if (!!this.swiperOptions.width && !this.swiperOptions.fullWidth) {
      return `${this.swiperOptions.width}px`;
    } else if (this.swiperOptions.fullWidth) {
      return '100%';
    } else {
      return `${this.computedWidth}px`;
    }
  }

  public get relativeHeight(): string {
    if (typeof this.swiperOptions.height === 'string' && this.swiperOptions.height.includes('vw')) {
      return  this.swiperOptions.height;
    } else {
      return `${this.swiperOptions.height}px`;
    }
  }

  public get swiperStyles(): Partial<CSSStyleDeclaration> {
    return {
      width: this.width,
      height: this.swiperOptions.fullHeight
        ? '100%'
        : this.relativeHeight,
    };
  }

  @NoCache
  public get isEnd(): boolean {
    return this.realIndex === 0 || this.realIndex === this.iData.length - 1;
  }

  @NoCache
  public get activeIndex(): number {
    return this.instanceActiveIndex || this.swiperOptions.startIndex;
  }
  public set activeIndex(value: number) {
    this.instanceActiveIndex = value;
  }

  @NoCache
  public get iData() {
    if (!!this.childs.length) { return this.chunks; }
    return this.instanceData || this.slides;
  }
  public set iData(value) {
    this.instanceData = value;
  }

  @NoCache
  public get leftEnd(): boolean {
    return !this.swiperOptions.loop && this.activeIndex === 0;
  }

  @NoCache
  public get rightEnd(): boolean {
    return !this.swiperOptions.loop && this.activeIndex === this.iData.length - 1;
  }

  @NoCache
  public get realIndex(): number {
    return !this.swiperOptions.loop ? this.activeIndex
          : this.activeIndex === 0  ? 0
          : this.activeIndex - 1;
  }

  @NoCache
  private get renderSlides(): boolean {
    return this.instanceRenderSlides;
  }
  private set renderSlides(value: boolean) {
    this.instanceRenderSlides = value;
  }

  public created() {
    this.swiperOptions = mergeDeep(this.initOptions, this.options);
    Object.freeze(this.swiperOptions);
  }

  public async slideNext(speed: number = this.swiperOptions.speed, callback?: () => void): Promise<boolean> {
    const calculateOffset = () => {
      this.offset = this.swiperOptions.slidesPerView === 'auto' ? -this.computedWidth * this.activeIndex
                    : this.instancePreviousIndex === 0            ? -this.computedWidth
                    : -this.computedWidth * (this.activeIndex - this.perView);
    };
    if (this.swiperOptions.effect === 'fade' && !!this.activeElement?.nextSibling) {
      this.activeElement.style.transition = `opacity 500ms ease-in-out`;
      if (this.activeElement.nextSibling instanceof HTMLElement) {
        this.activeElement.nextSibling.style.transition = `opacity 500ms ease-in-out`;
        this.activeElement.nextSibling.style.opacity = '1';
      }
      this.$nextTick(() => {
        this.instancePreviousIndex = this.activeIndex;
        this.activeIndex++;
        calculateOffset();
        if (this.activeElement?.previousSibling instanceof HTMLElement) {
          this.activeElement.previousSibling.style.transition = '';
          this.activeElement.previousSibling.style.opacity = '0';
        }
      });
      return true;
    }
    if (!this.swiperOptions.rtl && !!this.activeElement?.nextSibling) {
      this.instancePreviousIndex = this.activeIndex;
      this.activeIndex = this.swiperOptions.slidesPerView === 'auto'             ? this.activeIndex + 1
                        : (this.activeIndex + this.perView) >= this.iData.length ? this.iData.length - 1
                        : this.activeIndex + this.perView;
      calculateOffset();
      if (this.swiperOptions.effect === 'slide') {
        const result = await this.nextPreview(speed, callback);
        return result;
      }
      return true;
    } else {
      return false;
    }
  }

  public async slidePrev(speed: number = this.swiperOptions.speed, callback?: () => void): Promise<boolean> {
    const calculateOffset = () => {
      this.offset = this.offset < 0
                  ? this.offset + this.computedWidth
                  : this.offset - this.computedWidth;
    };
    if (this.swiperOptions.effect === 'fade' && !!this.activeElement?.previousSibling) {
      this.activeElement.style.transition = `opacity 500ms ease-in-out`;
      if (this.activeElement.previousSibling instanceof HTMLElement) {
        this.activeElement.previousSibling.style.transition = `opacity 500ms ease-in-out`;
        this.activeElement.previousSibling.style.opacity = '1';
      }
      this.$nextTick(() => {
        this.instancePreviousIndex = this.activeIndex;
        this.activeIndex--;
        calculateOffset();
        if (this.activeElement?.nextSibling instanceof HTMLElement) {
          this.activeElement.nextSibling.style.transition = '';
          this.activeElement.nextSibling.style.opacity = '0';
        }
      });
      return true;
    }
    if (!this.swiperOptions.rtl && !!this.activeElement?.previousSibling) {
      this.instancePreviousIndex = this.activeIndex;
      this.activeIndex = this.swiperOptions.slidesPerView === 'auto' ? this.activeIndex - 1
                        : (this.activeIndex - this.perView) < 0      ? 0
                        : this.activeIndex - this.perView;
      calculateOffset();
      if (this.swiperOptions.effect === 'slide') {
        const result = await this.nextPreview(speed, callback);
        return result;
      }
      return true;
    } else {
      return false;
    }
  }

  public slideToWithFade(index) {
    const next = this.wrapperElement.children.item(index);
    const previous = this.wrapperElement.children.item(this.activeIndex);
    (previous as any).style.transition = `opacity 500ms ease-in-out`;
    (next as any).style.transition = `opacity 500ms ease-in-out`;
    (next as any).style.opacity = '1';
    (previous as any).style.opacity = '0';
    setTimeout(() => {
      (previous as any).style.transition = '';
      (next as any).style.transition = '';
    }, 500);
    this.instancePreviousIndex = this.activeIndex;
    this.activeIndex = index;
    this.offset = this.offset > 0
      ? this.computedWidth * this.activeIndex
      : -this.computedWidth * this.activeIndex;
  }

  public async slideTo(index: number, speed: number = this.swiperSpeed, callback?: () => void): Promise<boolean> {
    if (this.swiperOptions.effect === 'fade') {
      this.slideToWithFade(index);
      return true;
    }
    if (this.iData.length <= 1) {
      this.activeIndex = 0;
      this.offset = 0;
    } else {
      this.activeIndex = index;
      this.offset = this.iData.length === 1 ? 0 : -this.computedWidth * this.activeIndex;
    }
    return await this.nextPreview(speed, callback);
  }

  private emitChange() {
    this.$emit('slide:changed', {
      activeIndex: this.activeIndex,
      realIndex: this.realIndex,
      leftEnd: this.leftEnd,
      rightEnd: this.rightEnd,
    });
  }

  private juggle() {
    if (this.swiperOptions.loop && this.iData.length > 1) {
      if (this.activeIndex === 0) {
        this.activeIndex = this.iData.length;
        this.offset = this.computedWidth * -this.activeIndex;
        this.wrapperElement.style.transition = '';
        this.wrapperElement.style.transform = `translate3d(${this.offset}px, 0px, 0px)`;
        return;
      }
      if (this.activeIndex > this.iData.length) {
        this.activeIndex = 1;
        this.offset = this.computedWidth * -1;
        this.wrapperElement.style.transition = '';
        this.wrapperElement.style.transform = `translate3d(${this.offset}px, 0px, 0px)`;
        return;
      }
    }
  }

  private async nextPreview(speed: number = 0, callback?: () => void): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const cb = () => {
          this.juggle();
          if (typeof callback === 'function') { callback(); }
          this.wrapperElement.removeEventListener('transitionend', cb);
          this.emitChange();
          resolve(true);
        };
        this.wrapperElement.addEventListener('transitionend', cb);
        this.wrapperElement.style.transition = `transform ${speed}ms cubic-bezier(0.215, 0.61, 0.355, 1)`;
        this.wrapperElement.style.transform = `translate3d(${this.offset}px, 0px, 0px)`;
      } catch (error) {
        console.error(error);
        reject(false);
      }
    });
  }

  private addFadeInAnimation(el: HTMLElement, speed: number = .7) {
    el.classList.add('fadeIn');
    this.fadeInAnimation = true;
  }

  private addFadeOutAnimation(el: HTMLElement, speed: number = .7) {
    el.classList.add('fadeOut');
    this.fadeOutAnimation = true;
  }

  private createResizeObserver(): void {
    if (window.hasOwnProperty('ResizeObserver')) {
      // @ts-ignore:next-line Cannot find name 'ResizeObserver'.ts(2304)
      const ro = new ResizeObserver((entries) => {
        this.requestAnimationID = window.requestAnimationFrame(() => {
          for (const entry of entries) {
            this.resized = entry.contentRect.width;
            this.offset = this.swiperOptions.effect === 'slide'
              ? -entry.contentRect.width * this.activeIndex
              : 0;
            this.wrapperElement.style.transform = `translate3d(${this.offset}px, 0px, 0px)`;
            if (!!this.wrapperElement.firstChild) {
              (this.wrapperElement.firstChild as HTMLStyleElement).style.width = `${this.resized}px`;
            }
            if (!!this.wrapperElement.lastChild) {
              (this.wrapperElement.lastChild as HTMLStyleElement).style.width = `${this.resized}px`;
            }
            if (!!this.swiperOptions.breakpoints) {
              const points: any = [];
              Object.keys(this.swiperOptions.breakpoints).forEach((key) => {
                points.push(parseInt(key, 10));
              });
              if (points.length === 1) {
                points.forEach((prop) => {
                  if (parseInt(prop, 10) >= (this as any).resized) {
                    this.perView = (this as any).swiperOptions.breakpoints[prop].slidesPerView;
                  }
                });
              }
              if (points.length > 1) {
                const array = points.filter((num) => num >= (this as any).resized);
                if (!array.length) {
                  this.perView = (this as any).swiperOptions.slidesPerView;
                  return;
                }
                const prop = Math.min(...array);
                this.perView = (this as any).swiperOptions.breakpoints[prop].slidesPerView;
              }
            }
          }
        });
      });
      ro.observe(this.$el);
    }
  }

  private resolveImageSourceSet(obj: object): string {
    return obj && `${obj[400]} 400w, ${obj[800]} 800w,
      ${obj[1300]} 1300w, ${obj[1900]} 1900w, ${obj[3600]} 3600w`;
  }

  private showElement(): void {
    this.display = 'visible';
    if (this.fadeInAnimation) {
      this.addFadeInAnimation(this.wrapperElement);
    }
  }

  private hideElement(): void {
    this.display = 'hidden';
    if (this.fadeOutAnimation) {
      this.addFadeOutAnimation(this.wrapperElement);
    }
  }

  private createFakeElements(): void {
    if (this.swiperOptions.loop && this.iData.length > 1) {
      this.$nextTick(() => {
        const wrapper: HTMLElement = this.wrapperElement as HTMLElement;
        const firstDonor: ChildNode = wrapper.firstChild as ChildNode;
        const lastDonor: ChildNode = wrapper.lastChild as ChildNode;
        const firstClone: Node = firstDonor.cloneNode(true);
        const lastClone: Node = lastDonor.cloneNode(true);
        const first: any = lastClone.firstChild;
        const last: any = firstClone.firstChild;
        first.srcset = this.resolveImageSourceSet(this.iData[this.iData.length - 1][this.imageKey]);
        last.srcset = this.resolveImageSourceSet(this.iData[0][this.imageKey]);
        first.onload = () => {
          first.classList.add('v-lazy-image-loaded');
        };
        last.onload = () => {
          last.classList.add('v-lazy-image-loaded');
        };
        wrapper.insertBefore(lastClone, firstDonor);
        wrapper.appendChild(firstClone);
        this.slideNext(0, this.showElement);
      });
    }
  }

  private beforeDestroy() {
    if (!!this.observer) {
      this.observer.disconnect();
    }
    if (this.requestAnimationID) {
      window.cancelAnimationFrame(this.requestAnimationID);
    }
  }

  private createObserver() {
    const config = { childList: true, attributes: true, subtree: true };
    const callback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const active = mutationsList[0].target.getElementsByClassName('active').item(0);
          if (!!active) {
            active.style.opacity = 1;
          }
        }
      }
    };
    this.observer = new MutationObserver(callback);
    this.observer.observe(this.wrapperElement, config);
  }

  private init(): void {
    this.hideElement();
    this.iData = this.slides;
    this.fadeInAnimation = true;
    this.renderSlides = true;
    this.createResizeObserver();
    if (this.swiperOptions.loop && this.slides.length > 1) {
      this.$nextTick(() => this.createFakeElements());
    }
    if (this.swiperOptions.effect === 'fade') {
      this.wrapperElement.style.transform = `translate3d(0px, 0px, 0px)`;
      this.createObserver();
    }
    this.$nextTick(() => {
      this.instanceContentLoaded = true;
      this.showElement();
      this.$emit('slide:changed', {
        activeIndex: this.activeIndex,
        realIndex: this.realIndex,
        leftEndL: this.leftEnd,
        rightEnd: this.rightEnd,
      });
    });
  }

  private calculateIndex(offset): number {
    if ((!this.swiperOptions.loop && this.leftEnd && offset > 0)
      || (!this.swiperOptions.loop && this.rightEnd && this.vector === 'next')) {
        return this.activeIndex;
    } else {
      this.activeIndex = Math.round(Math.abs(offset / this.computedWidth));
    }
    return this.activeIndex;
  }

  private fadeSlides(offset, el) {
    (this as any).activeElement.style.transition = '';
    if (!!this.prevElement) {
      (this as any).prevElement.style.transition = '';
    }
    const factor = 700;
    const effect = (num, off, rate) => {
      const coef = (Math.abs(off) / (rate + Math.abs(off)));
      return (num > 0 ? num - coef : num + coef).toFixed(2);
    };
    el.style.opacity = effect(1, offset, factor);
  }

  private fadeEndSlides(el) {
    if (!this.isDragged) { return; }
    (this as any).activeElement.style.transition = 'opacity .5s ease-in-out';
    this.$nextTick(() => {
      (this as any).activeElement.style.opacity = '1';
      if (!!(this as any).prevElement) {
        (this as any).prevElement.style.opacity = '0';
      }
    });
  }

  private swipe(ev, el?): void {
    if (this.swiperOptions.lock) { return; }
    const oldIndex = this.activeIndex;
    const x = ev.pageX || ev?.touches?.[0].pageX;
    const y = ev?.touches?.[0].clientY;
    const move = (event) => {
      const deltaY = Math.abs(y - (event.pageY || event?.touches?.[0].clientY));
      const deltaX = Math.abs(x - (event.pageX || event?.touches?.[0].pageX));
      if (deltaY > deltaX) { return; }
      this.isDragged = true;
      const offsetX = x - (event.pageX || event?.touches?.[0].pageX);
      const offset = this.offset - offsetX * this.swiperOptions.sensitivity;
      this.calculateIndex(offset);
      this.vector = offsetX > 0 ? 'next' : 'prev';
      if (this.swiperOptions.effect === 'fade') {
        this.fadeSlides(offsetX, el);
      }
      if (this.swiperOptions.effect === 'slide') {
        const style = this.wrapperElement.style;
        style.transition = 'ease-in-out';
        style.transform = `translate3d(${offset}px, 0px, 0px)`;
      }
    };
    const stop = (): void => {
      if (oldIndex !== this.activeIndex) {
        this.instancePreviousIndex = oldIndex;
      }
      if (this.swiperOptions.effect === 'slide') {
        this.slideTo(this.activeIndex, this.swiperSpeed);
      }
      if (this.swiperOptions.effect === 'fade') {
        this.offset = this.offset > 0
          ? this.computedWidth * this.activeIndex
          : -this.computedWidth * this.activeIndex;
        this.fadeEndSlides(el);
      }
      removeListeners(this.$el, listeners);
      this.isDragged = false;
    };
    const listeners: Listener[] = [
      { event: 'mousemove', callback: move },
      { event: 'touchmove', callback: move },
      { event: 'mouseup', callback: stop },
      { event: 'mouseleave', callback: stop },
      { event: 'touchend', callback: stop },
      { event: 'touchcancel', callback: stop },
    ];
    createListeners(this.$el, listeners);
  }

  private lazyInit() {
    if (!!this.$el) {
      const width = window.getComputedStyle(this.$el).width || '0';
      this.resized = parseInt(width, 10);
      this.init();
    }
  }

  @Watch('slides')
  private updateData(newValue) {
    if (arrayIsEmpty(newValue)) { return; }
    this.renderSlides = false;
    this.wrapperElement.innerHTML = '';
    (this as any).$refs.controls.innerHTML = '';
    this.offset = 0;
    this.wrapperElement.style.transition = '';
    this.wrapperElement.style.transform = `translate3d(${this.offset}px, 0px, 0px)`;
    this.iData = [];
    this.activeIndex = 0;
    this.$nextTick(() => this.init());
  }

  private mounted() {
    this.lazyInit();
  }

  private child(childrens?: any) {
    if (!childrens || !childrens.length) { return; }
    const marginRight = 34;
    const width = (this.computedWidth - ((this.perView - 1) * marginRight)) / this.perView;
    return childrens.map((it, index) => {
      if (!!it.elm) {
        it.elm.style.width = `${width}px`;
        if (this.perView > 1) {
          it.elm.style.marginRight = index + 1 < (this as any).perView && `${marginRight}px`;
        } else {
          it.elm.style.marginRight = '';
        }
      }
      return it;
    });
  }

  private render(createElement: CreateElement): VNode | boolean {

    const slides: VNode[] = this.iData.map((slide, index) => {
      return createElement('div', {
        class: [
          'swiper-slides-items',
          this.realIndex === index && 'active',
          this.swiperOptions.effect === 'fade' && 'fade',
        ],
        style: {
          width: `${this.computedWidth}px`,
          padding: this.swiperOptions.slidesPerView !== 'auto'
            ? this.iData.length === index ? false : `${this.swiperOptions.spaceBetweenSlides}px`
            : false,
        },
        on: {
          mousedown: (event) => {
            if (this.swiperOptions.effect === 'fade') {
              this.swipe(event, event.currentTarget);
            }
          },
        },
      }, [
        (!objectIsEmpty(this.$slots) && !!this.$slots.child)
        ? this.child(slide)
        : this.$slots.default || createElement(VLazyImage, {
          props: {
            src: slide[this.imageKey] && `${slide[this.imageKey][3600]}` || '',
            'src-placeholder': slide[this.imageKey] && `${slide[this.imageKey][400]}`,
            /* tslint:disable */
            srcset: slide[this.imageKey] && `${slide[this.imageKey][400]} 400w, ${slide[this.imageKey][800]} 800w,
              ${slide[this.imageKey][1300]} 1300w, ${slide[this.imageKey][1900]} 1900w, ${slide[this.imageKey][3600]} 3600w`,
            /* tslint:enable */
            sizes: `(max-width: 401px) 400px, (max-width: 801px) 800px,
              (max-width: 1301px) 1300px, (max-width: 1901px) 1900px, 3600px`,
          },
          attrs: {
            alt: slide.name,
          },
        }),
      ]);
    });


    const wrapper: VNode = createElement('div', {
      class: 'swiper-wrapper-container',
      style: {
        visibility: this.display,
      },
      ref: 'wrapper',
    }, [
      this.renderSlides && slides,
    ]);

    const navigationButtons: VNode[] | false = this.$slots.navigation ||
      [
        createElement(CntrlBtn, {
          props: {
            disabled: this.leftEnd,
            calibrateMargin: this.swiperOptions.navigation.calibrateMargin,
            left: true,
            icon: this.swiperOptions.navigation.icon,
          },
          on: {
            click: () => this.slidePrev(),
          },
        }),
        createElement(CntrlBtn, {
          props: {
            disabled: this.rightEnd,
            calibrateMargin: this.swiperOptions.navigation.calibrateMargin,
            icon: this.swiperOptions.navigation.icon,
          },
          on: {
            click: () => this.slideNext(),
          },
        }),
      ];

    const paginate: VNode | VNode[] = this.$slots.pagination || createElement(Pagination, {
      props: {
        pages: this.calculatePages,
        active: this.realIndex,
        clicked: this.swiperOptions.pagination.clicked,
      },
      on: {
        click: (index) => this.slideTo(this.swiperOptions.loop ? index + 1 : index),
      },
    });

    return createElement('div', {
      attrs: {
        component: 'swiper',
      },
      style: this.swiperStyles,
    }, [
      createElement(!!this.swiperOptions.linkTo ? 'a' : 'div', {
        attrs: {
          href: !!this.swiperOptions.linkTo && this.swiperOptions.linkTo,
          wrapper: 'swiper',
        },
        class: 'swiper',
        style: this.swiperStyles,
        on: {
          '!mouseup': (e) => {
            const baseURL = (this as any).$router.options.base;
            const elm = e.target;
            const push = () => {
              if (this.swiperOptions.linkTo.includes(baseURL)) {
                e.preventDefault();
                e.stopPropagation();
                this.$router.push(this.swiperOptions.linkTo.replace(baseURL, '/'));
              }
            };
            const dispatchUpEvent = () => {
              e.preventDefault();
              e.stopPropagation();
              this.$el.dispatchEvent(new Event('mouseup'));
            };
            if (elm.tagName === 'A' && !(this as any).isDragged) {
              dispatchUpEvent();
              const index = elm.href.indexOf(baseURL);
              const url = elm.href.slice(index + baseURL.length);
              this.$router.push(url);
            }
            if ((this as any).isDragged) {
              dispatchUpEvent();
            }
            if (!(this as any).isDragged && !!this.swiperOptions.linkTo) {
              push();
            }
          },
          '!click': (e) => {
            const activeElements: string[] = ['BUTTON', 'svg', 'path'];
            const tag = e.target.tagName;
            if (activeElements.includes(tag) && this.$el.contains(e.target)) {
              return;
            } else {
              e.preventDefault();
              e.stopPropagation();
            }
          },
          mousedown: (e) => {
            e.stopPropagation();
            e.preventDefault();
          },
          '!mousedown': (e) => {
            if (this.swiperOptions.effect === 'slide') {
              e.preventDefault();
              this.swipe(e);
              e.stopPropagation();
            }
          },
          touchstart: (e) => this.swipe(e, e.target),
        },
      }, [
        this.$slots.header,
        wrapper,
        this.$slots.bottom,
      ]),
      createElement('div', {
        ref: 'controls',
        class: [ 'controls', 'fadeIn', this.swiperOptions.mobile && 'mobile' ],
        style: {
          display: this.instanceContentLoaded && 'flex',
        },
      }, [
        !!this.renderSlides && !!this.swiperOptions.navigation && navigationButtons,
        !!this.renderSlides && !!this.swiperOptions.pagination && paginate,
      ]),
    ]);
  }
}
